import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Pages
import LoginPage from "./pages/LoginPage";

// Admin Pages
import DashboardPage from "./pages/admin/DashboardPage";
import CompaniesPage from "./pages/admin/CompaniesPage";
import SedesPage from "./pages/admin/SedesPage";
import FloorsZonesPage from "./pages/admin/FloorsZonesPage";
import SpacesPage from "./pages/admin/SpacesPage";
import UsersPage from "./pages/admin/UsersPage";
import TeamsAreasPage from "./pages/admin/TeamsAreasPage";
import CapacityPage from "./pages/admin/CapacityPage";
import RulesPage from "./pages/admin/RulesPage";
import SchedulerPage from "./pages/admin/SchedulerPage";
import AssignmentsPage from "./pages/admin/AssignmentsPage";
import RequestsPage from "./pages/admin/RequestsPage";
import WaitlistPage from "./pages/admin/WaitlistPage";
import ReportsPage from "./pages/admin/ReportsPage";
import AuditPage from "./pages/admin/AuditPage";
import BulkUploadPage from "./pages/admin/BulkUploadPage";

// Colaborador Pages
import ColaboradorCalendarPage from "./pages/colaborador/CalendarPage";
import ColaboradorPreferencesPage from "./pages/colaborador/PreferencesPage";
import ColaboradorRequestsPage from "./pages/colaborador/RequestsPage";
import ColaboradorHistoryPage from "./pages/colaborador/HistoryPage";
import ColaboradorNotificationsPage from "./pages/colaborador/NotificationsPage";

// Toaster
import { Toaster } from "sonner";

const HomeRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (user.role === "colaborador") {
    return <Navigate to="/colaborador" replace />;
  }
  
  return <Navigate to="/admin" replace />;
};

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          
          {/* Home Redirect */}
          <Route path="/" element={<HomeRedirect />} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={["superadmin", "admin"]}>
              <DashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/companies" element={
            <ProtectedRoute allowedRoles={["superadmin"]}>
              <CompaniesPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/sedes" element={
            <ProtectedRoute allowedRoles={["superadmin", "admin"]}>
              <SedesPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/floors" element={
            <ProtectedRoute allowedRoles={["superadmin", "admin"]}>
              <FloorsZonesPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/spaces" element={
            <ProtectedRoute allowedRoles={["superadmin", "admin"]}>
              <SpacesPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute allowedRoles={["superadmin", "admin"]}>
              <UsersPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/teams" element={
            <ProtectedRoute allowedRoles={["superadmin", "admin"]}>
              <TeamsAreasPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/capacity" element={
            <ProtectedRoute allowedRoles={["superadmin", "admin"]}>
              <CapacityPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/rules" element={
            <ProtectedRoute allowedRoles={["superadmin", "admin"]}>
              <RulesPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/scheduler" element={
            <ProtectedRoute allowedRoles={["superadmin", "admin"]}>
              <SchedulerPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/assignments" element={
            <ProtectedRoute allowedRoles={["superadmin", "admin"]}>
              <AssignmentsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/requests" element={
            <ProtectedRoute allowedRoles={["superadmin", "admin"]}>
              <RequestsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/waitlist" element={
            <ProtectedRoute allowedRoles={["superadmin", "admin"]}>
              <WaitlistPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/reports" element={
            <ProtectedRoute allowedRoles={["superadmin", "admin"]}>
              <ReportsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/audit" element={
            <ProtectedRoute allowedRoles={["superadmin", "admin"]}>
              <AuditPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/bulk-upload" element={
            <ProtectedRoute allowedRoles={["superadmin", "admin"]}>
              <BulkUploadPage />
            </ProtectedRoute>
          } />
          
          {/* Colaborador Routes */}
          <Route path="/colaborador" element={
            <ProtectedRoute allowedRoles={["colaborador"]}>
              <ColaboradorCalendarPage />
            </ProtectedRoute>
          } />
          <Route path="/colaborador/preferencias" element={
            <ProtectedRoute allowedRoles={["colaborador"]}>
              <ColaboradorPreferencesPage />
            </ProtectedRoute>
          } />
          <Route path="/colaborador/solicitudes" element={
            <ProtectedRoute allowedRoles={["colaborador"]}>
              <ColaboradorRequestsPage />
            </ProtectedRoute>
          } />
          <Route path="/colaborador/historial" element={
            <ProtectedRoute allowedRoles={["colaborador"]}>
              <ColaboradorHistoryPage />
            </ProtectedRoute>
          } />
          <Route path="/colaborador/notificaciones" element={
            <ProtectedRoute allowedRoles={["colaborador"]}>
              <ColaboradorNotificationsPage />
            </ProtectedRoute>
          } />
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
